'use client';
import { useGetAvatars } from '@/backend/avatars/get-avatars';
import { useGetUser } from '@/backend/user/get-user/get-user';

import PrivyWrapper from '@/components/auth/privy/PrivyWrapper';
import { loginMethods } from '@/constants';
import {
	useAvatars,
	UserVisibilityEnum,
	useUser,
	useUserStoreActions,
	useUserVisibility,
} from '@/store';
import { usePrivyStoreActions } from '@/store/privyStore';

import {
	useLoginWithOAuth,
	usePrivy,
	useUpdateAccount,
} from '@privy-io/react-auth';

import React, { useCallback, useEffect } from 'react';
import SetRandomAvatarForNewUsers from './SetRandomAvatarForNewUsers/SetRandomAvatarForNewUsers';
import SetCasinoCategories from './SetCasinoCategories/SetCasinoCategories';
import { useGetCategoryAllGames } from '@/backend/casino/get-category-all-games';
import { useGetProviders } from '@/backend/casino/get-casino-provider-list';
import { useGetSettings } from '@/backend/settings/get-settings';
import { log } from 'console';
import { useUpdatePrivyUser } from '@/backend/user/post-update-privy-user';

const PrivySetup = () => {
	return (
		<PrivyWrapper>
			<PrivyStoreSetup />
		</PrivyWrapper>
	);
};

export default PrivySetup;

const PrivyStoreSetup = () => {
	const user = useUser();

	const { getUser } = useGetUser();
	const { setUser, resetUserStore } = useUserStoreActions();
	const { getCategoryAllGames } = useGetCategoryAllGames();
	const { getProviders } = useGetProviders();
	const { getSettings } = useGetSettings();

	const {
		user: privyUser,
		authenticated,
		ready,
		getAccessToken,
		login,
		linkDiscord,
		unlinkDiscord,
		linkEmail,
		unlinkEmail,
		linkWallet,
		unlinkWallet,
		logout,
	} = usePrivy();
	const { initOAuth } = useLoginWithOAuth();

	const { updatePrivyUser } = useUpdatePrivyUser();

	const { updateEmail } = useUpdateAccount({
		onSuccess: () => {
			updatePrivyUser();
		},
	});

	const {
		setPrivyUser,
		setAuthenticated,
		setReady,
		setLogin,
		setLogout,
		setAccessToken,
		setPrivyFunctions,
		setPrivyLoginFunctions,
		setLinkEmail,
		resetPrivyStore,
	} = usePrivyStoreActions();

	const { getAvatars } = useGetAvatars();
	const userVisibility = useUserVisibility();

	const getToken = useCallback(async () => {
		const token = await getAccessToken();

		const getAndFormatAccessToken = async () => {
			const token = await getAccessToken();

			if (!token) {
				return null;
			}
			return `AccessToken=${token}`;
		};

		setReady(ready);
		setPrivyFunctions({
			getAccessToken: getAndFormatAccessToken,
			linkDiscord,
			unlinkDiscord,
			linkEmail,
			unlinkEmail,
			linkWallet,
			unlinkWallet,
			updateEmail,
		});

		if (token) {
			setAccessToken(token);

			getUser({ isInitialUserCreation: true });
		}
	}, [
		user,
		getAccessToken,
		setAccessToken,
		setReady,
		ready,
		getUser,
		linkDiscord,
		unlinkDiscord,
		linkEmail,
		unlinkEmail,
		linkWallet,
		unlinkWallet,
		setPrivyFunctions,
		updateEmail,
	]);

	useEffect(() => {
		// if (ready && !user && authenticated) {
		if (ready && authenticated && privyUser?.hasAcceptedTerms) {
			getToken();
		}
	}, [ready, authenticated, privyUser]);

	useEffect(() => {
		if (user && !authenticated && ready) {
			resetUserStore();
			resetPrivyStore();
		}
	}, [user, ready, authenticated]);

	useEffect(() => {
		setReady(ready);
		setAuthenticated(authenticated);
		setLogin(() => login({ loginMethods: loginMethods }));
		setPrivyLoginFunctions({
			// loginWithGoogle: () => login({ loginMethods: ['google'] }),
			loginWithGoogle: () => initOAuth({ provider: 'google' }),
			// loginWithDiscord: () => login({ loginMethods: ['discord'] }),
			loginWithDiscord: () => initOAuth({ provider: 'discord' }),
			loginWithWallet: () => login({ loginMethods: ['wallet'] }),
			// loginWithWallet: () => initOAuth({ provider: 'wallet' }),
		});
		setLogout(logout);
		setLinkEmail(linkEmail);
	}, [ready, authenticated, login]);

	useEffect(() => {
		if (
			userVisibility === UserVisibilityEnum.GEO_BLOCKED ||
			userVisibility === UserVisibilityEnum.MAINTENANCE
		) {
			setLogin(() => null);
			setReady(false);
			setAuthenticated(false);
			setLogout(null);
			setUser(null);
			if (authenticated && ready) {
				logout();
			}
		}
	}, [ready, logout, authenticated, userVisibility]);

	useEffect(() => {
		getProviders();
		getSettings();
		getAvatars();
	}, [privyUser]);

	useEffect(() => {
		setPrivyUser(privyUser);
	}, [privyUser]);

	return (
		<>
			<SetRandomAvatarForNewUsers />
			<SetCasinoCategories />
		</>
	);
};
